import { type FandomCmp, LoadTimeEvent, IdentityInfoEvent } from "common-types";
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { InstantConfigRepository } from "instant-config";
import { loadScript, pushTrack, UniversalStorage } from "utils";
import { isConsentGranted } from "../../helpers/isConsentGranted";

interface LiveConnectPluginAPI {
  /**
   * Waits for the library to load and returns the HEM (sha2-hashed email) value.
   *
   * Note:
   * 1) this method is async because it waits for the library to load
   * 2) this can return `null` if the liQ library returns `null` for `sha2`
   *
   * @returns Promise for HEM (sha2-hashed email) value
   */
  waitForHem: () => Promise<string | null>;
  /**
   * Does not wait for the library to load, and instead checks right away if the sha2
   * HEM from LiveConnect is available in localStorage.
   *
   * Note:
   * 1) This method will instantly return false if LiveConnect is disabled.
   * 2) This method is NOT asynchronous, because it does NOT wait for the library to load.
   *
   * @returns boolean indicating if the LiveConnect sha2 HEM is available in localStorage.
   * */
  checkIfHemAvailable: () => boolean;
}

declare global {
  interface Window {
    liQ?: LiQ;
  }
}

const SCRIPT_URL = "https://b-code.liadm.com/a-07ev.min.js";
const FALLBACK_QF = "0.3";
const ID_CONFIG_TO_KEY_MAP: Record<LiQResolveParams, string> = {
  unifiedId: "liveConnect-unifiedId",
  sha2: "liveConnect",
  md5: "liveConnect-md5",
  sha1: "liveConnect-sha1",
};
const LIVE_CONNECT_READY_EVENT = "LiveConnectPluginReady";
const getStorageKey = (id: LiQResolveParams): string =>
  ID_CONFIG_TO_KEY_MAP[id];

export type LiveConnectPluginConfig = Pick<
  InstantConfigRepository,
  | "icLiveConnect"
  | "icLiveConnectCachingStrategy"
  | "icIdentityPartners"
  | "icLiveConnectQf"
>;

export class LiveConnectPlugin extends AdEnginePlugin<LiveConnectPluginAPI> {
  private storage?: UniversalStorage | null = null;
  private libraryLoaded = false;
  private consent = false;

  constructor(private readonly config: LiveConnectPluginConfig) {
    super();
  }

  static isEnabled(config: LiveConnectPluginConfig): boolean {
    return (
      config?.icLiveConnect &&
      config?.icLiveConnectCachingStrategy &&
      !config?.icIdentityPartners
    );
  }

  private setupStorage(): void {
    if (!this.consent || this.storage) {
      return;
    }

    this.storage = new UniversalStorage(
      this.config.icLiveConnectCachingStrategy.type === "local"
        ? () => window.localStorage
        : () => window.sessionStorage,
    );

    if (this.shouldLoadScript()) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "live_connect_started",
        time: performance.now(),
      });
      loadScript({
        src: SCRIPT_URL,
        isAsyncOrDeferOtherwise: true,
        node: "first",
      }).then(() => {
        this.resolveAndTrackIds();
        pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
          eventName: "live_connect_loaded",
          time: performance.now(),
        });
      });
    } else {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "live_connect_cached",
        time: performance.now(),
      });
      // if we have all the data in storage, we can skip library load and dispatch the event immediately
      this.libraryLoaded = true;
      window.dispatchEvent(new Event(LIVE_CONNECT_READY_EVENT));
    }
  }

  /**
   * Retrieves the list of mandatory HEM (Hashed Email) types defined in the
   * caching strategy configuration.
   *
   * @returns {LiQResolveParams[]} An array of the configured HEM types that LiveConnect can source
   */
  private getHEMTypes(): LiQResolveParams[] {
    return this.config.icLiveConnectCachingStrategy
      .mandatoryParams as LiQResolveParams[];
  }

  private shouldLoadScript(): boolean {
    const params: LiQResolveParams[] = this.getHEMTypes();

    // check if any of the mandatory params is missing
    return params.some((id) => !this.isAvailableInStorage(id));
  }

  private resolveAndTrackIds(): void {
    window.liQ?.resolve(
      (result) => {
        for (const [id, value] of Object.entries(result)) {
          if (id === "unifiedId") {
            pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
              eventName: "live_connect_responded_uuid",
              time: performance.now(),
            });
          }
          this.storeId(id as LiQResolveParams, value);
        }
        this.libraryLoaded = true;
        window.dispatchEvent(new Event(LIVE_CONNECT_READY_EVENT));
      },
      (err) => {
        // TODO: Better error handling

        console.error(err);
      },
      {
        qf: this.config?.icLiveConnectQf?.toString() ?? FALLBACK_QF,
        resolve: ["sha2", "md5", "sha1", "unifiedId"],
      },
    );
  }

  private storeId(id: LiQResolveParams, value: string): void {
    if (this.isAvailableInStorage(id)) {
      return;
    }

    const storageKey = getStorageKey(id);
    this.storage?.setItem(
      storageKey,
      value,
      this.config.icLiveConnectCachingStrategy.ttl,
    );
    pushTrack(IdentityInfoEvent.IDENTITY_INFO, {
      partnerName: storageKey,
      partnerIdentityId: value,
    });
  }

  private isAvailableInStorage(id: LiQResolveParams): boolean {
    return !!this.storage?.getItem(getStorageKey(id));
  }

  private getFromStorage(id: LiQResolveParams): string | null {
    return this.storage?.getItem(getStorageKey(id)) ?? null;
  }

  hooks: Hooks = {
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.consent = true;
      this.setupStorage();
    },
  };

  api: LiveConnectPluginAPI = {
    waitForHem: () =>
      new Promise((resolve) => {
        // if the plugin is disabled, return null
        if (!this.consent) {
          resolve(null);
        }

        // if the library is already loaded or the data was already in storage, resolve immediately with what we have
        if (this.libraryLoaded) {
          resolve(this.getFromStorage("sha2"));
        } else {
          // otherwise the library is not loaded yet, so we need to wait for it
          window.addEventListener("LiveConnectPluginReady", () => {
            resolve(this.getFromStorage("sha2"));
          });
        }
      }),
    checkIfHemAvailable: () => {
      if (!LiveConnectPlugin.isEnabled(this.config)) {
        return false;
      }

      return this.isAvailableInStorage("sha2");
    },
  };
}
